/**
 * Bellary — Land- und Sprachwahl als zweistufige Schublade. Runde 53 (3.9.2026).
 *
 * ⚠️⚠️ WARUM ES DIESE KOMPONENTE GIBT.
 * Horizons `selectCountry` (assets/localization.js:94-99) setzt den Wert UND ruft
 * sofort `form.submit()`. Genauso `changeLanguage` (:107-115) und Enter in der
 * Liste (:59-68). Vorbild morenutrition.de laesst dagegen erst auswaehlen und
 * dann unten bestaetigen. Weil `form.submit()` JEDEN submit-Handler umgeht, ist
 * das kein Markup-Trick — die Klickziele muessen ausgetauscht werden.
 *
 * ⚠️⚠️ DIESE KOMPONENTE LIEGT AUSSEN UM localization-form-component, NIE DAZWISCHEN.
 * component.js:171 bindet eine ref nur, wenn die NAECHSTGELEGENE Komponente die
 * eigene Instanz ist. Ein Element von uns zwischen Formular und Liste wuerde
 * saemtliche refs umleiten — Suche, Filter und Live-Region waeren tot, und zwar
 * OHNE Fehlermeldung. Aus demselben Grund lesen wir die Felder hier per
 * querySelector statt ueber this.refs: die refs gehoeren der Horizon-Komponente.
 *
 * ⚠️ Deshalb adressieren die Klickziele im Markup ausdruecklich:
 * `on:click="#bellary-sprachwahl/waehle/DE"`. Ein leerer Selektor (`/waehle/DE`)
 * loest auf die naechste Komponente auf, und das waere localization-form-component.
 */

import { Component } from '@theme/component';

const SCHUBLADE = 'bellary-sprachschublade';

class BellarySprachwahl extends Component {
  /** Ebene 1 zurueckstellen, sobald die Schublade zugeht. */
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('theme-drawer:close', this.#beiSchliessen);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('theme-drawer:close', this.#beiSchliessen);
  }

  #beiSchliessen = (event) => {
    if (!(event.target instanceof HTMLElement) || event.target.id !== SCHUBLADE) return;
    this.removeAttribute('data-ebene');
  };

  get #formular() {
    return this.querySelector('form.localization-form');
  }

  get #landfeld() {
    return this.querySelector('input[name="country_code"]');
  }

  get #sprachfeld() {
    return this.querySelector('input[name="language_code"]');
  }

  /**
   * Schiebt Ebene 2 herein.
   * @param {string} name - 'land' oder 'sprache'
   */
  zeigeEbene(name) {
    this.setAttribute('data-ebene', name);
    // Fokus auf den Zurueck-Knopf der geoeffneten Ebene, damit die Tastatur folgt.
    this.querySelector(`[data-ebene-inhalt="${name}"] .bellary-sprachwahl__zurueck`)?.focus();
  }

  /** Zurueck auf Ebene 1. Der Fokus geht auf die Karte, die hergefuehrt hat. */
  zurueck() {
    const offen = this.getAttribute('data-ebene');
    this.removeAttribute('data-ebene');
    if (offen) this.querySelector(`[data-ebene-karte="${offen}"]`)?.focus();
  }

  /**
   * Land vormerken — ohne abzuschicken.
   * @param {string} iso
   */
  waehle(iso) {
    const feld = this.#landfeld;
    if (!feld) return;
    feld.value = iso;
    this.#markiere('[data-ebene-inhalt="land"] [data-value]', (el) => el.dataset.value === iso);
  }

  /**
   * Sprache vormerken — ohne abzuschicken.
   * @param {string} code
   */
  waehleSprache(code) {
    const feld = this.#sprachfeld;
    if (!feld) return;
    feld.value = code;
    this.#markiere('[data-ebene-inhalt="sprache"] [data-value]', (el) => el.dataset.value === code);
  }

  /**
   * ⚠️ EIGENES data-Attribut, NICHT aria-current und NICHT aria-checked.
   * `aria-current` bleibt der zuletzt WIRKSAME Zustand — daran haengt Horizons
   * Haekchen. Die Vormerkung ist ein anderer Zustand als die geltende
   * Einstellung; sonst saehe man nach dem Antippen nicht mehr, was gerade
   * wirklich gilt. `aria-checked` waere an einem role="option" ungueltig.
   */
  #markiere(auswahl, trifft) {
    for (const el of this.querySelectorAll(auswahl)) {
      el.dataset.vorgemerkt = trifft(el) ? 'true' : 'false';
    }
  }

  /** Erst hier wird abgeschickt. */
  uebernehmen() {
    const formular = this.#formular;
    if (!formular) return;
    // requestSubmit statt submit: loest die normale Absende-Kette aus.
    if (typeof formular.requestSubmit === 'function') formular.requestSubmit();
    else formular.submit();
  }
}

if (!customElements.get('bellary-sprachwahl')) {
  customElements.define('bellary-sprachwahl', BellarySprachwahl);
}

/**
 * ⚠️⚠️ ABDUNKELN AUCH AM SCHREIBTISCH.
 * theme-drawer.js:218-224 oeffnet nur UNTER 990 px mit showModal() — nur dann
 * gibt es ueberhaupt einen ::backdrop. Darueber oeffnet Horizon mit show() und
 * schiebt stattdessen die Seite per .page-wrapper--drawer-open zur Seite
 * (snippets/theme-drawer.liquid:90-101, und zwar OHNE zu pruefen, welche
 * Schublade es war). Entscheidung Firdaus, 3.9.2026: abdunkeln wie MORE.
 *
 * Wir fassen Horizons JS nicht an, sondern korrigieren nur fuer DIESE eine
 * Schublade nach: Seitenverschiebung zuruecknehmen und den Dialog modal
 * nachziehen. Faellt das durch, bleibt Horizons Standardverhalten uebrig —
 * die Schublade funktioniert weiter, sie dunkelt dann nur nicht ab.
 */
document.addEventListener('theme-drawer:open', (event) => {
  const schublade = event.target;
  if (!(schublade instanceof HTMLElement) || schublade.id !== SCHUBLADE) return;

  document.querySelector('.page-wrapper')?.classList.remove('page-wrapper--drawer-open');

  // ⚠️ Auf dem Handy fuehrt der Auslöser aus dem Menue heraus. Zwei offene
  // Schubladen uebereinander wuerden ueber die Stapelreihenfolge
  // entschieden — deshalb das Menue hier schliessen.
  const menue = document.querySelector('header-drawer');
  if (menue && typeof menue.close === 'function') menue.close();

  const panel = schublade.querySelector('dialog');
  if (!panel || panel.matches(':modal')) return;

  /* ⚠️⚠️ RUNDE 55 — DIE EINFAHRT MUSS NACHGEHOLT WERDEN.
     Horizon startet beim Oeffnen `drawer-slide-in`. Der Modal-Tausch direkt
     danach (close + showModal) verschluckt diese Animation: sie beginnt und
     ist im selben Frame vorbei. Am 3.9.2026 gemessen — die Schublade stand
     einfach da, ohne jede Bewegung. Firdaus: „soll clean animiert von rechts
     rauskommen und nicht so ploetzlich".

     ⚠️ Der Tausch selbst laeuft mit stillgelegter Animation
     (`theme-drawer__dialog-instant`), damit die Einfahrt nicht angerissen
     wird. Danach wird die Klasse SOFORT wieder entfernt — sie darf NICHT
     haengenbleiben: theme-drawer.js:327 wartet beim Schliessen auf
     `animationend`, und ohne Animation gibt es das nicht.

     ⚠️ `void panel.offsetWidth` erzwingt einen Layoutdurchlauf. Ohne ihn
     erkennt der Browser das Entfernen und Wiederhinzufuegen derselben
     Animationsklasse als „nichts geaendert" und startet sie nicht neu.

     ⚠️ Aufgeraeumt wird ueber `animationend`, NICHT ueber
     requestAnimationFrame: in einem verdeckten Fenster feuert rAF nicht, und
     genau daran ist die erste Fassung haengengeblieben. */
  panel.classList.add('theme-drawer__dialog-instant');
  try {
    panel.close();
    panel.showModal();
  } catch {
    /* Im Zweifel nichts tun — Horizons Standardverhalten bleibt gueltig. */
  }
  panel.classList.remove('theme-drawer__dialog-instant');

  /* ⚠️⚠️ ERST ENTFERNEN, DANN WIEDER SETZEN.
     theme-drawer.js:212 setzt `--opening` BEVOR es `theme-drawer:open`
     ausloest — wenn dieser Handler laeuft, steht die Klasse also laengst am
     Element. Ein blosses `add` ist dann ein No-op und startet nichts neu;
     am 3.9.2026 gemessen: `animationstart` feuerte kein einziges Mal, die
     Kante des Panels stand still. Entfernen, Layout erzwingen, neu setzen. */
  panel.classList.remove('theme-drawer__dialog--opening');
  void panel.offsetWidth;
  panel.classList.add('theme-drawer__dialog--opening');
  panel.addEventListener(
    'animationend',
    () => panel.classList.remove('theme-drawer__dialog--opening'),
    { once: true }
  );
});
